import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import './Cart.css';
import { proxyBucketUrl, handleImageError } from '../utils/imageUtils';

const Cart: React.FC = () => {
  const { state: cartState, removeFromCart, clearCart, getTotalItems, getTotalPrice } = useCart();

  if (cartState.loading) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-loading">
            <div className="loading-spinner"></div>
            <p>Loading your cart...</p>
          </div>
        </div>
      </div>
    );
  }

  if (cartState.items.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-empty">
            <div className="empty-cart-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4H20C20.5523 4 21 4.44772 21 5C21 5.55228 20.5523 6 20 6H19V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z" fill="#900e9b"/>
                <path d="M9 8V17H11V8H9ZM13 8V17H15V8H13Z" fill="#900e9b"/>
              </svg>
            </div>
            <h2>Your cart is empty</h2>
            <p>Discover unique vintage pieces and add them to your cart!</p>
            <Link to="/shop" className="continue-shopping-btn">Start Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <div className="cart-header">
          <h1 style={{display: 'flex', alignItems: 'center'}}>
            <img 
              src="/header-icons/shopper.png" 
              alt="Cart" 
              style={{width: '42px', height: '42px', marginRight: '16px', filter: 'invert(1)'}}
            />
            Shopping Cart
          </h1>
          <p className="cart-subtitle" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            Review your unique pieces and grab them before they're gone 
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 3C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.5783 8.50903 2.9987 7.05 3C5.59096 2.9987 4.19169 3.5783 3.16 4.61C2.1283 5.6417 1.5487 7.041 1.55 8.5C1.5487 9.959 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6053C22.3095 9.93789 22.4518 9.22248 22.45 8.5C22.4518 7.77752 22.3095 7.06211 22.0329 6.39467C21.7563 5.72723 21.351 5.1208 20.84 4.61Z" stroke="#900e9b" strokeWidth="2" fill="none"/>
            </svg>
          </p>
        </div>
        
        <div className="cart-content">
          <div className="cart-items">
            {cartState.items.map(item => (
              <div key={item.id} className="cart-item">
                <div className="item-image">
                  <img 
                    src={proxyBucketUrl(item.product?.images?.[0]) || '/products/placeholder.svg'} 
                    alt={item.product?.name || 'Product'} 
                    onError={handleImageError}
                  />
                </div>
                <div className="item-details">
                  <h3>{item.product?.name || 'Unknown Product'}</h3>
                  <p className="item-category">{item.product?.category?.name || 'Category'}</p>
                  {item.size && <p className="item-size">Size: {item.size}</p>}
                </div>
                <div className="item-actions">
                  <div className="item-price">€{Number(item.product?.price || 0).toFixed(2)}</div>
                  <button 
                    className="remove-item"
                    onClick={() => removeFromCart(item.id)}
                    disabled={cartState.loading}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H5H21" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H19Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="cart-summary">
            <div className="summary-header">
              <h3>Order Summary</h3>
            </div>
            <div className="summary-details">
              <div className="summary-row">
                <span>Subtotal ({getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}):</span>
                <span>€{getTotalPrice().toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping:</span>
                <span className="free">Free</span>
              </div>
              <div className="summary-row discount">
                <span>Vintage collection discount:</span>
                <span>-€{(getTotalPrice() * 0.05).toFixed(2)}</span>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-row total">
                <span>Total:</span>
                <span>€{(getTotalPrice() * 0.95).toFixed(2)}</span>
              </div>
            </div>
            
            <div className="cart-actions">
              <Link to="/shop" className="continue-shopping-btn">Continue Shopping</Link>
              <Link to="/checkout" className="checkout-btn">PROCEED TO CHECKOUT</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;